import { apiClient } from './api-client';
import type {
  TimeEntryTagResponse,
  TimeEntryTagsResponse,
  CreateTimeEntryTag,
  UpdateTimeEntryTag,
  PaginationParams,
  TimeEntryTagStatusEnum,
} from '@scrambl/shared';

export interface TimeEntryTagFilters extends PaginationParams {
  timeEntryId?: string;
  taggedUserId?: string;
  taggerUserId?: string;
  status?: TimeEntryTagStatusEnum;
}

export interface BulkCreateTagsRequest {
  timeEntryId: string;
  taggedUserIds: string[];
  message?: string;
}

export interface ReplaceTagsRequest {
  taggedUserIds: string[];
  message?: string;
}

export interface DismissTagRequest {
  reason?: string;
}

export interface RemoveTagRequest {
  reason?: string;
}

export interface DuplicateTagsRequest {
  sourceTimeEntryId: string;
  targetTimeEntryId: string;
}

export interface SuggestionFilters extends PaginationParams {
  status?: TimeEntryTagStatusEnum;
}

export const timeEntryTagApi = {
  /**
   * Get time entries where the current user is tagged (suggestions)
   */
  async getMySuggestions(filters: SuggestionFilters = {}): Promise<TimeEntryTagsResponse> {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, String(value));
      }
    });
    const endpoint = queryParams.toString()
      ? `/time-entry-tags/my-suggestions?${queryParams.toString()}`
      : '/time-entry-tags/my-suggestions';
    return await apiClient<TimeEntryTagsResponse>(endpoint);
  },

  /**
   * Get all time entry tags with pagination and filters
   */
  async getTimeEntryTags(filters: TimeEntryTagFilters = {}): Promise<TimeEntryTagsResponse> {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, String(value));
      }
    });
    const endpoint = queryParams.toString() ? `/time-entry-tags?${queryParams.toString()}` : '/time-entry-tags';
    return await apiClient<TimeEntryTagsResponse>(endpoint);
  },

  /**
   * Get a specific time entry tag by ID
   */
  async getTimeEntryTag(id: string): Promise<TimeEntryTagResponse> {
    return await apiClient<TimeEntryTagResponse>(`/time-entry-tags/${id}`);
  },

  /**
   * Get all tags for a specific time entry
   */
  async getTagsForTimeEntry(timeEntryId: string): Promise<TimeEntryTagsResponse> {
    return await apiClient<TimeEntryTagsResponse>(`/time-entry-tags/time-entry/${timeEntryId}`);
  },

  /**
   * Get active tags for a specific time entry
   */
  async getActiveTagsForTimeEntry(timeEntryId: string): Promise<TimeEntryTagsResponse> {
    return await apiClient<TimeEntryTagsResponse>(`/time-entry-tags/time-entry/${timeEntryId}/active`);
  },

  /**
   * Create a new time entry tag
   */
  async createTimeEntryTag(data: CreateTimeEntryTag): Promise<TimeEntryTagResponse> {
    return await apiClient<TimeEntryTagResponse>('/time-entry-tags', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Create multiple time entry tags at once
   */
  async bulkCreateTags(data: BulkCreateTagsRequest): Promise<TimeEntryTagsResponse> {
    return await apiClient<TimeEntryTagsResponse>('/time-entry-tags/bulk', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Replace all tags for a time entry
   */
  async replaceTagsForTimeEntry(
    timeEntryId: string,
    data: ReplaceTagsRequest
  ): Promise<TimeEntryTagsResponse> {
    return await apiClient<TimeEntryTagsResponse>(`/time-entry-tags/time-entry/${timeEntryId}/replace`, {
      method: 'PUT',
      body: data,
    });
  },

  /**
   * Update a time entry tag
   */
  async updateTimeEntryTag(id: string, data: UpdateTimeEntryTag): Promise<TimeEntryTagResponse> {
    return await apiClient<TimeEntryTagResponse>(`/time-entry-tags/${id}`, {
      method: 'PUT',
      body: data,
    });
  },

  /**
   * Dismiss a time entry tag (tagged user dismisses their own tag)
   */
  async dismissTag(id: string, data: DismissTagRequest = {}): Promise<TimeEntryTagResponse> {
    return await apiClient<TimeEntryTagResponse>(`/time-entry-tags/${id}/dismiss`, {
      method: 'PUT',
      body: data,
    });
  },

  /**
   * Remove a time entry tag (tagger or admin removes the tag)
   */
  async removeTag(id: string, data: RemoveTagRequest = {}): Promise<TimeEntryTagResponse> {
    return await apiClient<TimeEntryTagResponse>(`/time-entry-tags/${id}/remove`, {
      method: 'PUT',
      body: data,
    });
  },

  /**
   * Duplicate tags from one time entry to another
   */
  async duplicateTags(data: DuplicateTagsRequest): Promise<TimeEntryTagsResponse> {
    return await apiClient<TimeEntryTagsResponse>('/time-entry-tags/duplicate', {
      method: 'POST',
      body: data,
    });
  },

  /**
   * Delete a time entry tag (hard delete)
   */
  async deleteTimeEntryTag(id: string): Promise<{ message: string }> {
    return await apiClient<{ message: string }>(`/time-entry-tags/${id}`, {
      method: 'DELETE',
    });
  },
};